// src/utils/returningUserApiMapper.js

import { calculateNCB } from './apiHelpers';

// ---------- helpers ----------

const compact = (v) => (v === undefined || v === null ? '' : v);

const isMeaningful = (v) =>
  !(
    v === undefined ||
    v === null ||
    (typeof v === 'string' && v.trim() === '')
  );

const extractApiPayload = (raw) => {
  // Accept shapes: obj, [obj], {data: obj|[obj]}, {payload: obj|[obj]}
  const pickFirst = (x) => (Array.isArray(x) ? x[0] : x);
  if (raw && typeof raw === 'object') {
    if (Array.isArray(raw)) return pickFirst(raw);
    if (raw.data) return pickFirst(raw.data);
    if (raw.payload) return pickFirst(raw.payload);
  }
  return raw;
};

const safeSet = (obj, key, val, { allowEmpty = false } = {}) => {
  if (allowEmpty || isMeaningful(val)) obj[key] = val;
};

const setDeep = (obj, path, val, { allowEmpty = false } = {}) => {
  if (!allowEmpty && !isMeaningful(val)) return;
  const parts = String(path).split('.');
  let cur = obj;
  for (let i = 0; i < parts.length - 1; i++) {
    const k = parts[i];
    if (cur[k] == null || typeof cur[k] !== 'object') cur[k] = {};
    cur = cur[k];
  }
  cur[parts[parts.length - 1]] = val;
};

// optional: tiny pad
const pad2 = (v) => (v == null || v === '' ? '' : String(v).padStart(2, '0'));

const maritalMap = {
  1: 'Single',
  2: 'Married',
  3: 'PartneredCohabiting',
  4: 'Divorced',
  5: 'ApartSeperated',
  6: 'Widowed',
};

const genderMap = {
  1: 'Male',
  2: 'Female',
};

const licenseTypeMap = {
  1: "SA Driver's License",
  2: "SA Learner's License",
  3: 'International License',
};

// Maps the string license type values returned by RetrieveVehicleDrivers2_0
const licenseTypeStringMap = {
  RSA: "SA Driver's License",
  Learner: "SA Learner's License",
  International: 'International License',
};

const toYearMonthParts = (iso) => {
  const d = parseIsoDate(iso);
  return d
    ? { Year: d.getFullYear(), Month: d.getMonth() + 1 }
    : { Year: null, Month: null };
};

// const vehicleUseMap = { ... };

const formatPhone = (raw = '') => {
  const digits = String(raw).replace(/\D+/g, '');
  if (!digits) return '';
  return digits.replace(/^(\d{3})(\d{3})(\d{1,4}).*$/, (_, a, b, c) =>
    [a, b, c].filter(Boolean).join(' ')
  );
};

const parseIsoDate = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  return isNaN(d.getTime()) ? null : d;
};

const formatDateDisplay = (iso) => {
  const d = parseIsoDate(iso);
  if (!d) return '';
  const day = d.getDate();
  const month = d.toLocaleString('en-GB', { month: 'short' });
  const year = d.getFullYear();
  return `${day} ${month} ${year}`;
};

const dateParts = (iso) => {
  const d = parseIsoDate(iso);
  if (!d) return { day: null, month: null, year: null };
  return { day: d.getDate(), month: d.getMonth() + 1, year: d.getFullYear() };
};

const getExistingFormValues = () => {
  try {
    return JSON.parse(localStorage.getItem('formValues') || '{}');
  } catch {
    return {};
  }
};

const saveFormValues = (obj) => {
  localStorage.setItem('formValues', JSON.stringify(obj));
};

// ---------- NEW: small utils for fallback synthesis ----------

const pick = (obj, keys) => {
  for (const k of keys) {
    const v = k
      .split('.')
      .reduce((acc, p) => (acc && acc[p] != null ? acc[p] : undefined), obj);
    if (isMeaningful(v)) return v;
  }
  return '';
};

const parseDateDMonY = (s) => {
  if (!s) return null;
  const m = String(s)
    .trim()
    .match(/^(\d{1,2})\s+([A-Za-z]{3})\s+(\d{4})$/);
  if (!m) return null;
  const [, d, mon, y] = m;
  const map = {
    jan: '01',
    feb: '02',
    mar: '03',
    apr: '04',
    may: '05',
    jun: '06',
    jul: '07',
    aug: '08',
    sep: '09',
    oct: '10',
    nov: '11',
    dec: '12',
  };
  const mm = map[mon.toLowerCase()];
  if (!mm) return null;
  return { year: y, month: mm, day: String(d).padStart(2, '0') };
};

const normalizeYear = (v) => {
  if (!v && v !== 0) return '';
  const n = Number(v);
  return Number.isFinite(n) ? String(n) : String(v);
};

// ---------- config-driven rules ----------
const FIELD_RULES = [
  {
    targets: ['IDNumber', 'idNumber'],
    value: (api) => compact(api.IDNumber),
  },
  {
    targets: ['FirstName', 'firstName'],
    value: (api) => compact(api.FirstName),
  },
  {
    targets: ['LastName', 'surname'],
    value: (api) => compact(api.LastName),
  },
  {
    targets: ['fullName'],
    value: (api) => {
      const fn = compact(api.FirstName);
      const ln = compact(api.LastName);
      return [fn, ln].filter(Boolean).join(' ').trim();
    },
  },
  {
    targets: ['EmailAddress', 'email'],
    value: (api) => compact(api.EmailAddress),
  },
  {
    targets: ['IsEmailEmpty'],
    value: (api) => !compact(api.EmailAddress),
    allowEmpty: true,
  },
  {
    targets: ['CellNumber', 'phone'],
    value: (api) => formatPhone(api.CellNumber),
  },
  {
    targets: ['IsCellNumberEmpty'],
    value: (api) => !formatPhone(api.CellNumber),
    allowEmpty: true,
  },
  {
    targets: ['FullAddress', 'nightAddress', 'dayAddress'],
    value: (api) => compact(api.FullAddress),
  },
  {
    targets: ['IsFullAddressEmpty'],
    value: (api) => !compact(api.FullAddress),
    allowEmpty: true,
  },
  {
    targets: ['Gender', 'gender'],
    value: (api) => genderMap[api.GenderPK] || api.Gender || '',
  },
  {
    targets: ['MaritalStatus', 'maritalStatus'],
    value: (api) => maritalMap[api.MaritalStatusPk] || api.MaritalStatus || '',
  },
  {
    targets: ['DateOfBirth', 'dateOfBirth'],
    value: (api) => formatDateDisplay(api.DateOfBirth),
  },
  {
    targets: ['birthday.day'],
    value: (api) => {
      const fromApi = api.DobDay ?? null;
      const fromDob = dateParts(api.DateOfBirth).day;
      return pad2(fromApi ?? fromDob ?? '');
    },
  },
  {
    targets: ['birthday.month'],
    value: (api) => {
      const fromApi = api.DobMonth ?? null;
      const fromDob = dateParts(api.DateOfBirth).month;
      return pad2(fromApi ?? fromDob ?? '');
    },
  },
  {
    targets: ['birthday.year'],
    value: (api) => {
      const fromApi = api.DobYear ?? null;
      const fromDob = dateParts(api.DateOfBirth).year;
      return fromApi != null ? String(fromApi) : fromDob ? String(fromDob) : '';
    },
  },
  {
    targets: ['DobDay'],
    value: (api) => (api.DobDay ?? dateParts(api.DateOfBirth).day) || '',
  },
  {
    targets: ['DobMonth'],
    value: (api) => (api.DobMonth ?? dateParts(api.DateOfBirth).month) || '',
  },
  {
    targets: ['DobYear'],
    value: (api) => (api.DobYear ?? dateParts(api.DateOfBirth).year) || '',
  },
  {
    targets: ['hippoMarketingOptIn'],
    value: (api) =>
      api.MarketingCommunication === true ||
      api.MarketingCommunication === false
        ? api.MarketingCommunication
        : null,
  },
];

// ---------- vehicles & drivers ----------
const buildVehicles = (apiVehicles = []) => {
  const uniq = new Map();

  for (const v of apiVehicles) {
    const {
      VehicleKey,
      VehicleMake,
      VehicleModel,
      VehicleYear,
      VehicleDescription,
      VehicleUsePk,
    } = v || {};

    // --- Validation ---
    const requiredFields = [
      VehicleKey,
      VehicleMake,
      VehicleModel,
      VehicleYear,
      VehicleDescription,
    ];
    const hasAllRequired = requiredFields.every(
      (x) => x !== null && x !== undefined && String(x).trim() !== ''
    );
    if (!hasAllRequired) continue;

    // --- Build key ---
    const key = [
      VehicleYear,
      VehicleMake,
      VehicleModel,
      VehicleDescription,
      VehicleUsePk,
    ]
      .map((x) =>
        String(x ?? '')
          .trim()
          .toUpperCase()
      )
      .join('|');

    // --- Build normalized item ---
    const item = {
      VehicleYear,
      VehicleDescription,
      VehicleKey,
      VehicleMake,
      VehicleModel,
      VehicleUsePk,
      NightTimeAddress: v.NightTimeAddress || '',
      NightTimeSecuritySetup: v.NightTimeSecuritySetup || '',
      DayTimeAddress: v.DayTimeAddress || '',
      DayTimeSecuritySetup: v.DayTimeSecuritySetup || '',
      CarUse: v.CarUse || v.VehicleUse || '',
    };

    if (!uniq.has(key)) uniq.set(key, item);
  }

  return [...uniq.values()];
};

const buildDrivers = (apiVehicles = []) => {
  const uniq = new Map();
  const order = ['A', '0', '1', '2', '3', '4', '5', '6', '7'];

  const getRank = (val) => {
    if (val == null) return -1;
    const idx = order.indexOf(String(val).trim().toUpperCase());
    return idx === -1 ? -1 : idx;
  };

  for (const v of apiVehicles) {
    const Name = v.Name || '';
    const Surname = v.LastName || '';
    const DateOfBirth = v.DateOfBirth || '';

    const key = [Name, Surname, DateOfBirth]
      .map((x) =>
        String(x ?? '')
          .trim()
          .toUpperCase()
      )
      .join('|');

    const maritalStr =
      v.MaritalStatus && String(v.MaritalStatus) ? v.MaritalStatus : '';
    const genderStr = v.Gender && String(v.Gender) ? v.Gender : '';

    const licenseTypePk = v.LicenseTypePk ?? null;
    const licenseTypeName =
      licenseTypePk != null ? licenseTypeMap[licenseTypePk] || '' : '';

    const licenseDateParts = toYearMonthParts(v.DateLicenseObtained);

    const newDriver = {
      Name,
      Surname,
      FullName: [Name, Surname].filter(Boolean).join(' ').trim(),
      DateOfBirth,
      DateLicenseObtained: v.DateLicenseObtained || '',
      DateLicenseObtainedParts: licenseDateParts,
      LicenseTypePk: licenseTypePk,
      LicenseTypeName: licenseTypeName,
      MaritalStatus: maritalStr,
      Gender: genderStr,
      DriverTypePk: v.DriverTypePk,
      ComprehensiveInsDuration: v.ComprehensiveInsDuration,
      LastClaimDuration: v.LastClaimDuration,
      NoClaimBonus: v.NoClaimBonus,
    };

    if (!uniq.has(key)) {
      uniq.set(key, newDriver);
    } else {
      // merge existing and new
      const existing = uniq.get(key);

      const merged = {
        ...existing,
        ComprehensiveInsDuration:
          getRank(v.ComprehensiveInsDuration) >
          getRank(existing.ComprehensiveInsDuration)
            ? v.ComprehensiveInsDuration
            : existing.ComprehensiveInsDuration,
        LastClaimDuration:
          getRank(v.LastClaimDuration) > getRank(existing.LastClaimDuration)
            ? v.LastClaimDuration
            : existing.LastClaimDuration,
        NoClaimBonus:
          getRank(v.NoClaimBonus) > getRank(existing.NoClaimBonus)
            ? v.NoClaimBonus
            : existing.NoClaimBonus,
      };

      uniq.set(key, merged);
    }
  }

  return [...uniq.values()];
};

// ---------- builds drivers from the new separate Drivers array (RetrieveVehicleDrivers2_0) ----------
export const buildDriversFromSeparateArray = (apiDrivers = []) => {
  const uniq = new Map();
  const order = ['A', '0', '1', '2', '3', '4', '5', '6', '7'];

  const getRank = (val) => {
    if (val == null) return -1;
    const idx = order.indexOf(String(val).trim().toUpperCase());
    return idx === -1 ? -1 : idx;
  };

  for (const d of apiDrivers) {
    const Name = d.DriverName || '';
    const Surname = d.DriverLastName || '';
    const DateOfBirth = d.DateOfBirth || '';

    const key = [Name, Surname, DateOfBirth]
      .map((x) =>
        String(x ?? '')
          .trim()
          .toUpperCase()
      )
      .join('|');

    const licenseTypeName =
      licenseTypeStringMap[d.LicenseType] || d.LicenseType || '';
    const licenseDateParts = toYearMonthParts(d.DateLicenseObtained);

    const newDriver = {
      Name,
      Surname,
      FullName: [Name, Surname].filter(Boolean).join(' ').trim(),
      DateOfBirth,
      DateLicenseObtained: d.DateLicenseObtained || '',
      DateLicenseObtainedParts: licenseDateParts,
      LicenseTypePk: null,
      LicenseTypeName: licenseTypeName,
      MaritalStatus: d.MaritalStatus || '',
      Gender: d.Gender || '',
      DriverTypePk: d.DriverTypePk || null,
      ComprehensiveInsDuration: d.ComprehensiveInsDuration,
      LastClaimDuration: d.LastClaimDuration,
      NoClaimBonus: d.NoClaimBonus || null,
    };

    if (!uniq.has(key)) {
      uniq.set(key, newDriver);
    } else {
      const existing = uniq.get(key);
      const merged = {
        ...existing,
        ComprehensiveInsDuration:
          getRank(d.ComprehensiveInsDuration) >
          getRank(existing.ComprehensiveInsDuration)
            ? d.ComprehensiveInsDuration
            : existing.ComprehensiveInsDuration,
        LastClaimDuration:
          getRank(d.LastClaimDuration) > getRank(existing.LastClaimDuration)
            ? d.LastClaimDuration
            : existing.LastClaimDuration,
      };
      uniq.set(key, merged);
    }
  }

  return [...uniq.values()];
};

const buildPets = (apiPets = []) =>
  (Array.isArray(apiPets) ? apiPets : []).map((pet) => ({
    name: pet?.PetName || pet?.name || '',
    type: pet?.PetType || pet?.type || '',
    gender: pet?.PetGender || pet?.Gender || pet?.gender || '',
    breed: pet?.PetBreed || pet?.Breed || pet?.breed || '',
    colour: pet?.PetColour || pet?.Colour || pet?.colour || '',
    dob: pet?.DOB || pet?.dob || '',
  }));

// ---------- NEW: fallback builders from existing formValues ----------
function buildFallbackDriver(values) {
  const fullName = String(values?.fullName || '').trim();
  const [firstGuess, ...rest] = fullName ? fullName.split(/\s+/) : [];

  const firstName = values?.FirstName || firstGuess || '';
  const lastName = values?.LastName || rest.join(' ') || '';

  // ---- DOB (prefer split parts; then fallback to "29 Jun 1990" style)
  let y =
    String(values?.DobYear ?? pick(values, ['birthDate.year']) ?? '') || '';
  let m =
    String(values?.DobMonth ?? pick(values, ['birthDate.month']) ?? '') || '';
  let d = String(values?.DobDay ?? pick(values, ['birthDate.day']) ?? '') || '';

  if (!y || !m || !d) {
    const parsed = parseDateDMonY(values?.DateOfBirth || values?.dateOfBirth);
    if (parsed) {
      y ||= parsed.year;
      m ||= parsed.month;
      d ||= parsed.day;
    }
  }

  const isoDOB =
    y && m && d
      ? `${String(y).padStart(4, '0')}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`
      : '';

  // ---- Durations (main-person fields only)
  const comp = values?.uninterruptedCarInsurance;
  const claim = values?.lastClaimYears;

  return {
    Name: firstName,
    Surname: lastName,
    FullName: [firstName, lastName].filter(Boolean).join(' ').trim(),
    DateOfBirth: isoDOB,
    DriverTypePk: 1,
    ComprehensiveInsDuration: comp,
    LastClaimDuration: claim,
    NoClaimBonus: calculateNCB(comp, claim),
  };
}

function buildFallbackVehicle(values) {
  // Prefer the most precise fields you keep in formValues
  const year =
    normalizeYear(
      pick(values, ['carDetails.VehicleYear', 'yearOfManufacturer'])
    ) || '';

  const desc =
    pick(values, ['carDetails.SearchDescription', 'carToInsure']) ||
    [values?.carDetails?.VehicleMake, values?.carDetails?.VehicleModel]
      .filter(Boolean)
      .join(' ') ||
    '';

  const key = pick(values, ['carDetails.VehicleKey']) || '';
  const make = pick(values, ['carDetails.VehicleMake']) || '';
  const model = pick(values, ['carDetails.VehicleModel']) || '';
  const usePk = 2; // 2 == "Personal" in your examples

  return {
    VehicleYear: year ? Number(year) : year, // keep numeric when possible
    VehicleDescription: String(desc).toUpperCase(), // match your API normalisation
    VehicleKey: key,
    VehicleMake: make || null,
    VehicleModel: model || null,
    VehicleUsePk: usePk,
    NightTimeAddress: values?.nightAddress || '',
    NightTimeSecuritySetup: values?.nightSecurity || '',
    DayTimeAddress: values?.dayAddress || '',
    DayTimeSecuritySetup: values?.daySecurity || '',
    CarUse: values?.vehicleUsage || '',
  };
}

export function synthesizeFallbackLists(values) {
  const drivers = [buildFallbackDriver(values)];
  const vehicles = [buildFallbackVehicle(values)];
  return { drivers, vehicles };
}

// ---------- main entry ----------
export function updateFormValuesFromApi(
  apiResponseRaw,
  currentFormValues = {}
) {
  console;
  const existing = getExistingFormValues();

  // base has the newest answers first, then anything from storage
  const base = { ...existing, ...currentFormValues };
  const apiResponse = extractApiPayload(apiResponseRaw);
  const next = { ...base };

  const hasUsefulApi =
    apiResponse &&
    (isMeaningful(apiResponse.IDNumber) ||
      isMeaningful(apiResponse.FirstName) ||
      (Array.isArray(apiResponse.Vehicles) &&
        apiResponse.Vehicles.length > 0) ||
      (Array.isArray(apiResponse.Pets) && apiResponse.Pets.length > 0));

  if (hasUsefulApi) {
    for (const rule of FIELD_RULES) {
      const val = rule.value(apiResponse);
      for (const t of rule.targets) {
        const existingVal = t.includes('.') ? pick(next, [t]) : next[t];
        if (isMeaningful(existingVal)) continue; // don't overwrite meaningful values
        if (t.includes('.')) {
          setDeep(next, t, val, { allowEmpty: !!rule.allowEmpty });
        } else {
          safeSet(next, t, val, { allowEmpty: !!rule.allowEmpty });
        }
      }
    }

    if (Array.isArray(apiResponse.Vehicles) && apiResponse.Vehicles.length) {
      next.Vehicles = buildVehicles(apiResponse.Vehicles);
      // Prefer separate Drivers array (new RetrieveVehicleDrivers2_0 format),
      // otherwise fall back to extracting drivers from the Vehicles array (old format).
      if (Array.isArray(apiResponse.Drivers) && apiResponse.Drivers.length) {
        next.Drivers = buildDriversFromSeparateArray(apiResponse.Drivers);
      } else {
        next.Drivers = buildDrivers(apiResponse.Vehicles);
      }
    } else {
      // payload present but no arrays → synthesize from the freshest base
      const { drivers, vehicles } = synthesizeFallbackLists(base);
      if (!next.Vehicles?.length) next.Vehicles = vehicles;
      if (!next.Drivers?.length) next.Drivers = drivers;
    }

    if (Array.isArray(apiResponse.Pets) && apiResponse.Pets.length) {
      if (!Array.isArray(next.Pets) || next.Pets.length === 0) {
        next.Pets = apiResponse.Pets;
      }
      if (!Array.isArray(next.pets) || next.pets.length === 0) {
        next.pets = buildPets(apiResponse.Pets);
      }
      if (!isMeaningful(next.numberOfPets)) {
        next.numberOfPets = apiResponse.Pets.length;
      }
    }
  } else {
    // fully synthesize from the freshest base
    const { drivers, vehicles } = synthesizeFallbackLists(base);
    next.Vehicles = vehicles;
    next.Drivers = drivers;
  }

  saveFormValues(next);
  return next;
}
